import { Code, Group, ScrollArea, Table } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundMessage } from '../../components/DataTable';
import { LoadingRows } from '../../components/LoadingRows';
import { PageHeader } from '../../components/PageHeader';
import { Paper } from '../../components/Paper';
import { SearchField } from '../../components/SearchField';
import { Title } from '../../components/Title';
import { useResponsiveSpace } from '../../hooks/useResponsiveSpace';
import { query } from '../../util/api/query';
import { ExportButton } from '../export/ExportButton';
import { GroupTotalRow } from './components/GroupTotalRow';
import type { GroupTotalDto } from './types/GroupDtos';
import { KeyedRow } from '../../components/KeyedRow';

export function GroupTotals() {
	const { organisation } = useParams();

	const [searchQuery, setSearchQuery] = useState('');

	const groupsQuery = query<GroupTotalDto[]>(
		`${organisation}/group-totals/search-groups-with-stats?searchValue=${searchQuery}`,
	);

	const isDataFound = useMemo(() => {
		return groupsQuery.data?.value && groupsQuery.data?.value.length > 0;
	}, [groupsQuery.data?.value]);

	const responsiveHeight = useResponsiveSpace([
		[753, '68vh'],
		[1048, '78vh'],
	]);

	return (
		<>
			<PageHeader title={'Group Totals'} hideFiltering>
				<Group justify={'flex-end'}>
					<SearchField
						placeholder={'Search groups'}
						onDelayComplete={(value) => setSearchQuery(value)}
					/>
					<ExportButton
						apiPath={`${organisation}/group-totals/export`}
						title={'Export Group & Product Sales'}
					/>
				</Group>
			</PageHeader>
			<Code style={{ backgroundColor: 'transparent' }}>
				Groups with no associated sales will not appear.
			</Code>
			<Paper>
				<ScrollArea w={'100%'} h={responsiveHeight}>
					<Table
						highlightOnHover={isDataFound}
						striped={isDataFound}
						stickyHeader
					>
						<Table.Thead>
							<KeyedRow>
								<Table.Th>
									<Title label={'Name'} />
								</Table.Th>
								<Table.Th>
									<Title label={'Quantity Sold'} />
								</Table.Th>
								<Table.Th>
									<Title label={'Net Sales'} />
								</Table.Th>
								<Table.Th>
									<Title label={'Gross Sales'} />
								</Table.Th>
								<Table.Th>
									<Title label={'VAT'} />
								</Table.Th>
								<Table.Th>
									<Title label={'Discount'} />
								</Table.Th>
								<Table.Th w={'6vw'}>
									<Title label={'Departments'} />
								</Table.Th>
							</KeyedRow>
						</Table.Thead>
						<Table.Tbody>
							{groupsQuery.isLoading ? (
								<LoadingRows rows={20} />
							) : (
								<>
									{isDataFound ? (
										<>
											{groupsQuery.data?.value.map((g) => (
												<GroupTotalRow key={g.id} group={g} />
											))}
										</>
									) : (
										<KeyedRow>
											<Table.Td colSpan={4}>
												<NoDataFoundMessage />
											</Table.Td>
										</KeyedRow>
									)}
								</>
							)}
						</Table.Tbody>
					</Table>
				</ScrollArea>
			</Paper>
		</>
	);
}
